
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const ADURegulationsLosGatos = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Los Gatos ADU Regulations",
    "description": "Learn the ADU zoning, setbacks, lot size, and permitted unit sizes for Los Gatos. Updated for 2025.",
    "publisher": {
      "@type": "Organization",
      "name": "UDA ADU",
      "url": "https://www.udaadu.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.udaadu.com/logo.png"
      }
    },
    "mainEntity": {
      "@type": "WebPageElement",
      "name": "Los Gatos ADU Zoning and Building Rules"
    },
    "url": "https://www.udaadu.com/adu-regulations-los-gatos"
  };

  return <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/adu-regulations-los-gatos" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(structuredData)
    }} />
      
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>}

      <main className="max-w-6xl mx-auto px-4 py-8">
        <section className="text-center py-16">
          {/* Hero Section */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-light mb-5 text-white">
            ADU Regulations in Los Gatos
          </h1>

          <p className="text-lg md:text-xl leading-relaxed max-w-4xl mx-auto text-white mb-8">
            Discover the specific rules and requirements for building an ADU in Los Gatos. From zoning and lot size to setbacks and max square footage — this guide summarizes what you need to know before you build.
          </p>

          <div className="mb-8">
            <Link to="/">
              <Button className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-4 rounded-xl shadow-lg transition-colors font-thin mx-[13px] text-xl px-[20px]">
                🔍 Search My Property Address
              </Button>
            </Link>
          </div>

          {/* Regulations Content */}
          <div className="mt-10 mb-10 bg-white rounded-xl p-8 max-w-4xl mx-auto text-left">
            <h2 className="text-2xl font-medium text-[#e4683b] mb-4">Zoning Regulations</h2>
            <p className="text-gray-800 mb-6">ADUs are permitted in all residential zones in Los Gatos.</p>

            <h2 className="text-2xl font-medium text-[#e4683b] mb-4 mt-6">Lot Size & Coverage</h2>
            <p className="text-gray-800 mb-6">No minimum lot size required. ADUs up to 1,200 sq. ft. are allowed.</p>

            <h2 className="text-2xl font-medium text-[#e4683b] mb-4 mt-6">Setback Requirements</h2>
            <p className="text-gray-800 mb-6">Minimum 4 ft. side and rear setbacks required.</p>

            <h2 className="text-2xl font-medium text-[#e4683b] mb-4 mt-6">Permitted ADU Sizes</h2>
            <p className="text-gray-800">Maximum ADU size up to 1,200 sq. ft. or 50% of primary dwelling, whichever is less.</p>
          </div>

          {/* Video Section */}
          <div className="mb-10">
            <video autoPlay muted loop playsInline disablePictureInPicture onContextMenu={() => false} controlsList="nodownload nofullscreen noremoteplay" className="w-full max-w-4xl mx-auto rounded-3xl shadow-2xl" style={{
            pointerEvents: 'none'
          }}>
              <source src="https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/VIDEOS/uda-santa-clara-model-adu-video.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8zMWY3NTE2YS1mMTNjLTQ2MzEtYTliMC1mOWJmMGFjNTBkZjMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJWSURFT1MvdWRhLXNhbnRhLWNsYXJhLW1vZGVsLWFkdS12aWRlby5tcDQiLCJpYXQiOjE3NDk4MjM0NjYsImV4cCI6MjA2NTE4MzQ2Nn0.6j9a3WgKBNaWvqzI8BYfSDDzkmYFno9qllvOCEmBKWc" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>

          {/* CTA Section */}
          <div className="mb-10">
            <Button onClick={openCalendlyModal} className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-4 rounded-xl shadow-lg transition-colors font-normal text-base px-[8px]">
              Book Your Complimentary ADU Consultation
            </Button>
          </div>

          {/* Footer Link */}
          <div className="mt-10">
            <p className="text-white text-base">
              Need more answers? Visit our{" "}
              <Link to="/faq" className="text-[#FF7A00] underline hover:no-underline">
                Frequently Asked Questions
              </Link>
              .
            </p>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingADUWizard />
      <FloatingReserveButton />
    </div>;
};

export default ADURegulationsLosGatos;
